<template>
  <b-modal
    id="modal-kpi-result-detail"
    size="lg"
    scrollable
    hide-footer
    header-class="kpi-modal__header"
    body-class="kpi-modal__body"
    @show="onShow"
    @hidden="rows = []"
  >
    <template #modal-title>
      <div class="kpi-modal__title">Kết quả nhập chỉ số</div>
      <div v-if="item" class="kpi-modal__subtitle">
        <span class="kpi-modal__name">{{ item.fullName }}</span>
        <span v-if="phone" class="kpi-modal__dot">·</span>
        <span v-if="phone">{{ phone }}</span>
        <span v-if="item.class" class="kpi-modal__class">{{ item.class }}</span>
      </div>
    </template>

    <!-- Mặc định 14 ngày để lần mở đầu luôn nhẹ; "Tất cả" mới kéo toàn bộ lịch sử -->
    <div class="kpi-toolbar">
      <div class="kpi-segment">
        <button
          v-for="range in ranges"
          :key="range.value"
          type="button"
          class="kpi-segment__item"
          :class="{ active: selectedRange === range.value }"
          @click="selectedRange = range.value"
        >
          {{ range.text }}
        </button>
      </div>
      <div class="kpi-toolbar__select">
        <basic-select
          placeholder="Tất cả chỉ số"
          name="modalKpiType"
          :options="kpiTypes"
          track-by="value"
          value-label="text"
          :value.sync="selectedKpiType"
          :clear="true"
          class="mb-0"
        />
      </div>
    </div>

    <div v-if="loading" class="kpi-empty">Đang tải dữ liệu...</div>
    <div v-else-if="!rows.length" class="kpi-empty">
      Không có lần nhập nào {{ selectedRange ? `trong ${selectedRange} ngày gần nhất` : '' }}
    </div>

    <!-- Mỗi loại chỉ số một nhóm, mặc định thu gọn; tiêu đề hiện luôn lần nhập gần nhất -->
    <div
      v-for="group in groups"
      :key="group.type"
      class="kpi-group"
      :class="{ open: openGroups[group.type] }"
      :style="{ '--kpi-color': group.color }"
    >
      <div class="kpi-group__header" @click="toggleGroup(group.type)">
        <span class="kpi-group__dot" />
        <span class="kpi-group__name">{{ group.name }}</span>
        <span class="kpi-group__count">{{ group.rows.length }} lần</span>
        <span class="kpi-group__latest">
          Gần nhất: <b>{{ group.rows[0].data }}</b> · {{ formatTime(group.rows[0], group.dateOnly) }}
        </span>
        <span class="kpi-group__chevron">
          <inline-svg src="/media/svg/icons/Neolex/Arrows/chevron-bottom.svg" />
        </span>
      </div>
      <b-collapse :visible="!!openGroups[group.type]">
        <table class="kpi-table">
          <thead>
            <tr>
              <th style="width: 48px">#</th>
              <th>Giá trị</th>
              <th v-if="group.hasImages" style="width: 220px">Hình ảnh</th>
              <th style="width: 160px">Thời gian</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, index) in group.rows" :key="index">
              <td class="text-muted">{{ index + 1 }}</td>
              <td class="kpi-table__value">{{ row.data }}</td>
              <!-- Giống màn user_dashboard: thumbnail nhỏ, bấm để xem ảnh lớn -->
              <td v-if="group.hasImages">
                <div class="d-flex flex-wrap">
                  <img
                    v-for="image in row.images || []"
                    :key="image.id"
                    :src="image.url"
                    class="kpi-thumb"
                    alt=""
                    @click.stop="handlePreview(image.url)"
                  />
                </div>
              </td>
              <td class="text-muted">{{ formatTime(row, group.dateOnly) }}</td>
            </tr>
          </tbody>
        </table>
      </b-collapse>
    </div>
    <image-preview :image.sync="imagePreview" />
  </b-modal>
</template>

<script>
import ImagePreview from '@/view/pages/coaches_experts/components/ModalPreview.vue';

export default {
  name: 'ModalKpiResultDetail',
  components: {
    'image-preview': ImagePreview,
  },
  props: {
    item: {
      type: Object,
      default: null,
    },
    // Chỉ số đang lọc ở màn list, lấy làm giá trị mặc định khi mở modal
    kpiType: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      loading: false,
      rows: [],
      imagePreview: null,
      // Nhóm chỉ số đang mở, theo kpiType
      openGroups: {},
      selectedKpiType: null,
      // Số ngày gần nhất; 0 = tất cả
      selectedRange: 14,
      ranges: [
        { text: '7 ngày', value: 7 },
        { text: '14 ngày', value: 14 },
        { text: '30 ngày', value: 30 },
        { text: '90 ngày', value: 90 },
        { text: 'Tất cả', value: 0 },
      ],
      // dateOnly: bảng chỉ lưu ngày, giờ hiện ra là do lệch múi giờ nên không hiển thị
      kpiTypes: [
        { text: 'Đường huyết', value: 'glucose', color: '#e5484d' },
        { text: 'Huyết áp', value: 'bp', color: '#8e4ec6' },
        { text: 'Cân nặng', value: 'weight', color: '#0090ff' },
        { text: 'Dinh dưỡng', value: 'diet', color: '#f59e0b' },
        { text: 'Vận động', value: 'exercise', color: '#21a567', dateOnly: true },
        { text: 'HbA1c', value: 'hba1c', color: '#d6409f' },
      ],
    };
  },
  watch: {
    // Đổi bộ lọc trong modal thì tải lại; bỏ qua lần gán từ onShow vì onShow đã tự tải
    selectedKpiType() {
      if (this.syncingFromList) return;
      this.loadData();
    },
    selectedRange() {
      if (this.syncingFromList) return;
      this.loadData();
    },
  },
  computed: {
    // Gom theo loại chỉ số, giữ đúng thứ tự trong dropdown
    groups() {
      return this.kpiTypes
        .map((kpi) => {
          const rows = this.rows.filter((row) => row.kpiType === kpi.value);
          return {
            type: kpi.value,
            name: kpi.text,
            color: kpi.color,
            dateOnly: !!kpi.dateOnly,
            // Chỉ hiện cột Hình ảnh khi nhóm có ít nhất một lần nhập kèm ảnh
            hasImages: rows.some((row) => row.images && row.images.length),
            rows,
          };
        })
        .filter((group) => group.rows.length);
    },
    phone() {
      const phone = this.item?.phoneNumber;
      return phone ? phone.replace('+84', '0') : null;
    },
  },
  methods: {
    handlePreview(url) {
      this.imagePreview = url;
      this.$bvModal.show('image-preview');
    },
    toggleGroup(type) {
      this.$set(this.openGroups, type, !this.openGroups[type]);
    },
    // Mở modal: lấy sẵn chỉ số đang lọc ở list, các nhóm thu gọn lại
    onShow() {
      this.openGroups = {};
      this.syncingFromList = true;
      this.selectedKpiType = this.kpiType;
      this.selectedRange = 14;
      this.$nextTick(() => {
        this.syncingFromList = false;
      });
      this.loadData();
    },
    async loadData() {
      if (!this.item?.accountId) return;

      this.loading = true;
      try {
        const { meta, data, error } = await this.$api.get('/Lesson/KpiResultDetail', {
          params: {
            accountId: this.item.accountId,
            kpiType: this.selectedKpiType,
            // Unix timestamp (giây) đầu ngày của N ngày trước, tính cả hôm nay
            dateFrom: this.selectedRange
              ? this.$moment().subtract(this.selectedRange - 1, 'days').startOf('date').format('X')
              : null,
          },
        });
        if (!meta.success) {
          return this.$toastr.e({ title: 'Lỗi !', msg: error.message });
        }
        this.rows = data || [];
      } catch (error) {
        this.$toastr.e({ title: 'Lỗi !', msg: error.message });
      } finally {
        this.loading = false;
      }
    },
    // BE trả DateTime dạng unix timestamp (giây)
    formatTime(row, dateOnly) {
      if (!row?.time) return null;
      return this.convertTimestampToDate(row.time, dateOnly ? 'DD/MM/YYYY' : 'DD/MM/YYYY HH:mm');
    },
  },
};
</script>

<style lang="scss" scoped>
.kpi-modal__title {
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
}

.kpi-modal__subtitle {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 4px;
  font-size: 13px;
  font-weight: 400;
  color: #6b7280;
}

.kpi-modal__name {
  color: #374151;
  font-weight: 500;
}

.kpi-modal__dot {
  color: #c0c4cc;
}

.kpi-modal__class {
  margin-left: 4px;
  padding: 1px 8px;
  border-radius: 10px;
  background: #e4f5f5;
  color: #01645a;
  font-size: 12px;
}

.kpi-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}

.kpi-toolbar__select {
  width: 200px;
}

// Segmented control nhẹ thay cho nhóm nút viền đậm
.kpi-segment {
  display: inline-flex;
  padding: 3px;
  border-radius: 8px;
  background: #f3f4f6;
}

.kpi-segment__item {
  border: 0;
  background: transparent;
  padding: 5px 12px;
  border-radius: 6px;
  font-size: 13px;
  color: #6b7280;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;

  &:hover {
    color: #374151;
  }

  &.active {
    background: #fff;
    color: #01645a;
    font-weight: 600;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
  }
}

.kpi-empty {
  padding: 32px 0;
  text-align: center;
  color: #9ca3af;
}

.kpi-group {
  border: 1px solid #eef0f4;
  border-left: 3px solid var(--kpi-color);
  border-radius: 8px;
  margin-bottom: 10px;
  background: #fff;
}

.kpi-group__header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  cursor: pointer;
  user-select: none;

  &:hover {
    background: #fafbfc;
  }
}

.kpi-group__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--kpi-color);
  flex-shrink: 0;
}

.kpi-group__name {
  font-weight: 600;
  color: #1f2937;
}

.kpi-group__count {
  padding: 1px 8px;
  border-radius: 10px;
  background: #f3f4f6;
  color: #6b7280;
  font-size: 12px;
}

.kpi-group__latest {
  margin-left: auto;
  font-size: 13px;
  color: #6b7280;

  b {
    color: #374151;
    font-weight: 600;
  }
}

.kpi-group__chevron {
  display: inline-flex;
  color: #9ca3af;
  transition: transform 0.2s;

  ::v-deep svg {
    width: 16px;
    height: 16px;
  }
}

.kpi-group.open .kpi-group__chevron {
  transform: rotate(180deg);
}

.kpi-table {
  width: 100%;
  border-top: 1px solid #eef0f4;
  font-size: 13px;

  th {
    padding: 8px 14px;
    background: #fafbfc;
    color: #9ca3af;
    font-weight: 500;
    font-size: 12px;
    text-align: left;
  }

  td {
    padding: 9px 14px;
    border-top: 1px solid #f3f4f6;
  }

  tbody tr:hover {
    background: #fafbfc;
  }
}

.kpi-thumb {
  width: 40px;
  height: 40px;
  margin: 2px 8px 2px 0;
  border-radius: 6px;
  object-fit: cover;
  cursor: pointer;
}

.kpi-table__value {
  color: #1f2937;
  font-weight: 500;
}
</style>
